import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "../../Business/AuthenticationBusiness";
import { SwipeableDrawer } from "@mui/material";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import { Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import Person from "@mui/icons-material/Person";
import { useAuthUser } from "../../Context/AuthUserContext";
import { alpha, useTheme } from "@mui/material/styles";

const menuRowTextSx = {
  borderRadius: "10px",
  mx: 1,
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: "rgba(24, 32, 104, 0.06)",
  },
  "& .MuiListItemText-primary": {
    fontSize: "1rem",
    fontWeight: 500,
    color: "#182068",
  },
};

export default function Drawer(props) {
  const theme = useTheme();
  const location = useLocation();
  const drawerSurface = theme.brand?.surface || theme.palette.primary.main;
  const textOnSurface = theme.brand?.textOnSurface || theme.palette.primary.contrastText;

  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [state, setState] = useState(false);
  const [login, setLogin] = useState(false);
  const hideGuestNavbarMenus = location.pathname === "/login";

  const { authReady, isLoggedIn, profile, profileLoading } = useAuthUser();
  const apiData = profile?.userPhoto || "";
  const navbarPending = !authReady || (isLoggedIn && profileLoading && !profile);

  useEffect(() => {
    setLogin(props.login);
  }, [props.login]);
  useEffect(() => {
    setState(props.state);
  }, [props.state]);

  function handleClick() {
    setOpenDrawer(!openDrawer);
  }
  async function signOutAction() {
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  function toggleDrawer(nextOpen) {
    return function (event) {
      if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
        return;
      }
      setState(nextOpen);
    };
  }

  function displayName() {
    const fn = (profile?.firstName || "").trim();
    const ln = (profile?.lastName || "").trim();
    const full = [fn, ln].filter(Boolean).join(" ");
    return full || "Bezohminds Member";
  }

  function drawerHeader() {
    if (!login) return null;
    const goProfile = () => {
      navigate("/my-profile");
      setState(false);
    };
    return (
      <ButtonBase
        component="div"
        onClick={goProfile}
        focusRipple
        aria-label="Open my profile"
        sx={{
          display: "block",
          width: "100%",
          textAlign: "center",
          backgroundColor: drawerSurface,
          color: textOnSurface,
          pt: 3,
          pb: 2.5,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Avatar
          src={apiData || undefined}
          alt=""
          sx={{
            width: 72,
            height: 72,
            mx: "auto",
            bgcolor: "#fff",
            border: `2px solid ${alpha("#ffffff", 0.25)}`,
          }}
        >
          {!apiData ? <Person sx={{ fontSize: 40, color: "grey.500" }} /> : null}
        </Avatar>
        <Typography
          sx={{
            mt: 1.5,
            color: textOnSurface,
            fontWeight: 600,
            fontSize: "1.05rem",
            lineHeight: 1.35,
          }}
        >
          {displayName()}
        </Typography>
      </ButtonBase>
    );
  }

  function list() {
    return (
      <Box
        sx={{
          width: "min(82vw, 320px)",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
        role="presentation"
      >
        {drawerHeader()}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
          onKeyDown={toggleDrawer(false)}
        >
          <List disablePadding sx={{ py: { xs: 1.25, md: 1 }, width: "100%" }}>
            {getMenuList()}
          </List>
        </Box>
      </Box>
    );
  }

  function logoutRow() {
    return (
      <>
        <Divider sx={{ my: 1, mx: 1.5, borderColor: "grey.200" }} />
        <ListItemButton
          sx={{
            px: 2.5,
            py: 1.25,
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            ...menuRowTextSx,
          }}
          onClick={() => {
            signOutAction();
            setState(false);
          }}
        >
          <ListItemText primary="Logout" />
        </ListItemButton>
      </>
    );
  }

  function myProfileRow() {
    return (
      <Link to="/my-profile" style={{ textDecoration: "none", color: "inherit" }}>
        <ListItemButton sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }} onClick={toggleDrawer(false)}>
          <ListItemText primary="My Profile" />
        </ListItemButton>
      </Link>
    );
  }

  function getMenuList() {
    if (login) {
      return (
        <>
          {nestedListRoleBasedQuickLinks()}
          {myProfileRow()}
          {logoutRow()}
        </>
      );
    }
    if (hideGuestNavbarMenus || navbarPending || login == null) {
      return (
        <Link to="/scan-qr" style={{ textDecoration: "none", color: "inherit" }}>
          <ListItemButton
            onClick={toggleDrawer(false)}
            sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }}
          >
            <ListItemText primary="Scan QR Code" />
          </ListItemButton>
        </Link>
      );
    }
    return (
      <>
        <Link to="/scan-qr" style={{ textDecoration: "none", color: "inherit" }}>
          <ListItemButton
            onClick={toggleDrawer(false)}
            sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }}
          >
            <ListItemText primary="Scan QR Code" />
          </ListItemButton>
        </Link>
        <Link to="/login" style={{ textDecoration: "none", color: "inherit" }}>
          <ListItemButton
            onClick={toggleDrawer(false)}
            sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }}
          >
            <ListItemText primary="Login" />
          </ListItemButton>
        </Link>
        <Link to="/register" style={{ textDecoration: "none", color: "inherit" }}>
          <ListItemButton
            onClick={toggleDrawer(false)}
            sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }}
          >
            <ListItemText primary="Register" />
          </ListItemButton>
        </Link>
      </>
    );
  }

  function nestedListRoleBasedQuickLinks() {
    return null;
  }

  function nestedListMyCamiCodeSubMenuDrawer() {
    const defaultColor = "inherit";
    return (
      <>
        <ListItemButton onClick={handleClick} sx={{ px: 2.5, py: 1.25, ...menuRowTextSx }}>
          <ListItemText primary="My QR Codes" />
          {openDrawer ? <ExpandLess sx={{ color: "grey.600" }} /> : <ExpandMore sx={{ color: "grey.600" }} />}
        </ListItemButton>
        <Collapse in={openDrawer} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <Link to="created-qrcode" style={{ textDecoration: "none", color: defaultColor }}>
              <ListItemButton sx={{ pl: 4, py: 1, ...menuRowTextSx }} onClick={toggleDrawer(false)}>
                <ListItemText primary="Created QR Codes" primaryTypographyProps={{ sx: { color: "grey.600", fontWeight: 500 } }} />
              </ListItemButton>
            </Link>
            <Link to="received-qrcode" style={{ textDecoration: "none", color: defaultColor }}>
              <ListItemButton sx={{ pl: 4, py: 1, ...menuRowTextSx }} onClick={toggleDrawer(false)}>
                <ListItemText primary="Received QR Codes" primaryTypographyProps={{ sx: { color: "grey.600", fontWeight: 500 } }} />
              </ListItemButton>
            </Link>
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <SwipeableDrawer
      anchor="right"
      open={state}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}
      disableBackdropTransition={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      PaperProps={{
        sx: {
          width: "min(82vw, 320px)",
          maxWidth: "82vw",
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          boxSizing: "border-box",
        },
      }}
    >
      {list()}
    </SwipeableDrawer>
  );
}
